import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/users`, { params: q ? { q } : {} });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, ...patch } : u)));
      toast.success("Kullanıcı güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-6xl mx-auto p-5 lg:p-10" data-testid="admin-users">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Kullanıcılar</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
          Kayıtlı Kullanıcılar
        </h1>

        <div className="mt-6 flex gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="E-posta veya isim ara"
              className="pl-9 rounded-xl"
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="user-search"
            />
          </div>
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="user-search-btn">
            Ara
          </Button>
        </div>

        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          {users === null && (
            <div className="p-10 text-center text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          )}
          {users && users.length === 0 && (
            <div className="p-10 text-center text-zinc-500">Kullanıcı bulunamadı</div>
          )}
          {users && users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3 font-semibold">Kullanıcı</th>
                    <th className="px-4 py-3 font-semibold">Rol</th>
                    <th className="px-4 py-3 font-semibold">Sınıf</th>
                    <th className="px-4 py-3 font-semibold">Plan</th>
                    <th className="px-4 py-3 font-semibold">Durum</th>
                    <th className="px-4 py-3 font-semibold">Kayıt</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-900">{u.name}</div>
                        <div className="text-xs text-zinc-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.role === "admin" ? "bg-[#1E3A8A]/10 text-[#1E3A8A]" : "bg-zinc-100 text-zinc-700"
                        }`}>
                          {u.role === "admin" ? "Admin" : "Öğrenci"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{u.grade_level || "—"}</td>
                      <td className="px-4 py-3">
                        <Select value={u.plan} onValueChange={(v) => updateUser(u.id, { plan: v })}>
                          <SelectTrigger className="h-8 w-[120px] text-xs rounded-lg" data-testid={`user-plan-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Ücretsiz</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={u.account_status} onValueChange={(v) => updateUser(u.id, { account_status: v })}>
                          <SelectTrigger className="h-8 w-[120px] text-xs rounded-lg" data-testid={`user-status-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Aktif</SelectItem>
                            <SelectItem value="disabled">Devre Dışı</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">
                        {new Date(u.created_at).toLocaleDateString("tr-TR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
